import { useEffect, useState } from "react";
import { EMISSIONS_API_URL } from "./utils/apiConfig";
import { readEmissionsCache, writeEmissionsCache } from "./utils/emissionsCache";

// Components
import GreenOneDashboardV2 from "./components/dashboard-v2/GreenOneDashboardV2";
import HeroSection from "./components/landing/HeroSection";
import InfoSection from "./components/landing/InfoSection";
import IntroVideo from "./components/landing/IntroVideo";
import PartnerCarousel from "./components/landing/PartnerCarousel";
import AppFooter from "./components/ui/AppFooter";
import AppHeader from "./components/ui/AppHeader";
import CookieBanner from "./components/ui/CookieBanner";
import Eingabemaske from "./components/upload/Eingabemaske";
import UploadFile from "./components/upload/UploadFile";

// Auth Components
import ConfirmSignUp from "./components/auth/ConfirmSignUp";
import ForgotPassword from "./components/auth/ForgotPassword";
import SignIn from "./components/auth/SignIn";
import SignUp from "./components/auth/SignUp";

// Amplify
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signOut,
} from "aws-amplify/auth";

import {
  hideProcessingNotification,
  showProcessingErrorNotification,
  showProcessingNotification,
  showProcessingSuccessNotification,
} from "./utils/notifications";

import {
  identifyClarityUser,
  setClarityCompanyTag,
} from "./utils/clarity";

function normalizePrefetchedEmissionsPayload(data) {
  let parsed;

  try {
    if (Array.isArray(data)) {
      parsed = data;
    } else if (typeof data?.body === "string") {
      parsed = JSON.parse(data.body);
    } else if (Array.isArray(data?.body)) {
      parsed = data.body;
    } else if (Array.isArray(data?.rows)) {
      parsed = data;
    } else {
      parsed = data ?? {};
    }
  } catch {
    parsed = {};
  }

  if (Array.isArray(parsed)) {
    return { rows: parsed };
  }

  if (parsed && Array.isArray(parsed.rows)) {
    return parsed;
  }

  return { rows: [] };
}

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function serializeEmissionsPayload(data) {
  return JSON.stringify(normalizePrefetchedEmissionsPayload(data));
}

function App({ initialShowRegister = false }) {
  const [showForm] = useState(false);
  const [showEingabemaske, setShowEingabemaske] = useState(false);
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(initialShowRegister);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [prefetchedData, setPrefetchedData] = useState(null);
  const [buildingRelationship, setBuildingRelationship] = useState("");
  const [dashboardDataVersion, setDashboardDataVersion] = useState(0);

  const [
    pendingDashboardRefreshVersion,
    setPendingDashboardRefreshVersion,
  ] = useState(null);

  const [
    renderedDashboardRefreshVersion,
    setRenderedDashboardRefreshVersion,
  ] = useState(null);

  async function prefetchEmissionsData({
    useCacheFallback = true,
    commit = true,
  } = {}) {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const res = await fetch(
        EMISSIONS_API_URL,
        {
          cache: "no-store",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        throw new Error(
          "Die Dashboard-Daten konnten nicht geladen werden."
        );
      }

      const data = await res.json();
      const normalizedData =
        normalizePrefetchedEmissionsPayload(data);

      if (commit) {
        setPrefetchedData(normalizedData);

        writeEmissionsCache(normalizedData);
      }

      return normalizedData;
    } catch (err) {
      console.warn("Prefetch failed:", err);

      if (useCacheFallback) {
        const cached = readEmissionsCache();

        if (cached) {
          const parsedCache =
            normalizePrefetchedEmissionsPayload(
              cached
            );

          if (commit) {
            setPrefetchedData(parsedCache);
          }

          return parsedCache;
        }
      }

      throw err;
    }
  }

  async function prefetchUpdatedEmissionsData(previousData) {
    const previousSerialized =
      serializeEmissionsPayload(previousData);

    const maxAttempts = 20;
    const retryDelayMs = 3000;

    for (
      let attempt = 1;
      attempt <= maxAttempts;
      attempt += 1
    ) {
      const normalizedData =
        await prefetchEmissionsData({
          useCacheFallback: false,
          commit: false,
        });

      const hasRows =
        Array.isArray(normalizedData?.rows) &&
        normalizedData.rows.length > 0;

      const hasChanged =
        serializeEmissionsPayload(normalizedData) !==
        previousSerialized;

      if (hasRows && hasChanged) {
        setPrefetchedData(normalizedData);

        writeEmissionsCache(normalizedData);

        return normalizedData;
      }

      if (attempt < maxAttempts) {
        await wait(retryDelayMs);
      }
    }

    throw new Error(
      "Die neuen Dashboard-Daten sind noch nicht verfügbar."
    );
  }

  async function handleSavedAndReturnToDashboard(
    savedFormData
  ) {
    if (savedFormData?.buildingRelationship) {
      setBuildingRelationship(
        savedFormData.buildingRelationship
      );

      localStorage.setItem(
        "buildingRelationship",
        savedFormData.buildingRelationship
      );
    }

    setShowEingabemaske(false);
    setShowUploadFile(false);
    setShowDashboard(true);
    setRenderedDashboardRefreshVersion(null);

    showProcessingNotification({
      message:
        "Daten werden verarbeitet und das Dashboard wird aktualisiert.",
    });

    try {
      const cachedData = readEmissionsCache();

      const previousData =
        prefetchedData ??
        normalizePrefetchedEmissionsPayload(
          cachedData || {}
        );

      localStorage.removeItem("emissionsCache");
      setPrefetchedData(null);

      await prefetchUpdatedEmissionsData(previousData);

      const nextVersion =
        dashboardDataVersion + 1;

      setPendingDashboardRefreshVersion(nextVersion);
      setDashboardDataVersion(nextVersion);
    } catch (err) {
      console.warn(
        "Dashboard refresh after save failed:",
        err
      );

      setPrefetchedData(null);
      setPendingDashboardRefreshVersion(null);
      setRenderedDashboardRefreshVersion(null);

      showProcessingErrorNotification({
        message:
          "Die Eingaben wurden gespeichert, aber die neuen Dashboard-Daten konnten noch nicht geladen werden.",
      });
    }
  }

  useEffect(() => {
    if (
      pendingDashboardRefreshVersion &&
      renderedDashboardRefreshVersion ===
      pendingDashboardRefreshVersion
    ) {
      showProcessingSuccessNotification({
        message: "Dashboard wurde aktualisiert.",
      });

      setPendingDashboardRefreshVersion(null);
      setRenderedDashboardRefreshVersion(null);
    }
  }, [
    pendingDashboardRefreshVersion,
    renderedDashboardRefreshVersion,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const user = await getCurrentUser();
        const attributes =
          await fetchUserAttributes();

        const fullUser = {
          ...user,
          attributes,
        };

        const savedBuildingRelationship =
          localStorage.getItem(
            "buildingRelationship"
          ) || "";

        setCurrentUser(fullUser);
        identifyClarityUser(user);
        setClarityCompanyTag(
          attributes?.["custom:company"]
        );
        setBuildingRelationship(
          savedBuildingRelationship
        );
        setShowRegister(false);
        setShowDashboard(true);

        prefetchEmissionsData();
      } catch {
        // not logged in
      }
    })();
  }, []);

  function handleConsentChange(consent) {
    if (!consent.analytics || !currentUser) return;

    identifyClarityUser(currentUser);
    setClarityCompanyTag(
      currentUser.attributes?.["custom:company"]
    );
  }

  async function handleLoginClick() {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      setCurrentUser({
        ...user,
        attributes,
      });
      identifyClarityUser(user);
      setClarityCompanyTag(
        attributes?.["custom:company"]
      );

      setShowDashboard(true);

      prefetchEmissionsData();
    } catch {
      setShowLogin(true);
    }
  }

  async function handleRegisterClick() {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      setCurrentUser({
        ...user,
        attributes,
      });
      identifyClarityUser(user);
      setClarityCompanyTag(
        attributes?.["custom:company"]
      );

      setShowDashboard(true);

      prefetchEmissionsData();
    } catch {
      setShowRegister(true);
    }
  }

  const isLandingPage =
    !showForm &&
    !showEingabemaske &&
    !showUploadFile &&
    !showDashboard &&
    !showLogin &&
    !showRegister &&
    !showConfirm &&
    !showForgotPassword;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppHeader
        onLoginClick={isLandingPage ? handleLoginClick : undefined}
        onRegisterClick={isLandingPage ? handleRegisterClick : undefined}
      />

      <div style={{ flex: 1 }}>
        <CookieBanner onConsentChange={handleConsentChange} />

        {/* Landing Page */}
        {isLandingPage && (
          <>
            <HeroSection
              onLoginClick={handleLoginClick}
              onRegisterClick={handleRegisterClick}
            />

            <PartnerCarousel />
            <IntroVideo />
            <InfoSection onRegisterClick={handleRegisterClick} />
          </>
        )}

        {/* Login */}
        {showLogin && (
          <SignIn
            onSuccess={async (user) => {
              const attributes =
                await fetchUserAttributes();

              const savedBuildingRelationship =
                localStorage.getItem(
                  "buildingRelationship"
                ) || "";

              setCurrentUser({
                ...user,
                attributes,
              });
              identifyClarityUser(user);
              setClarityCompanyTag(
                attributes?.["custom:company"]
              );

              setBuildingRelationship(
                savedBuildingRelationship
              );

              setShowLogin(false);
              setShowDashboard(true);

              prefetchEmissionsData();
            }}
            onRegister={() => {
              setShowLogin(false);
              setShowRegister(true);
            }}
            onForgotPassword={() => {
              setShowLogin(false);
              setShowForgotPassword(true);
            }}
          />
        )}

        {/* Passwort vergessen */}
        {showForgotPassword && (
          <ForgotPassword
            onBack={() => {
              setShowForgotPassword(false);
              setShowLogin(true);
            }}
            onResetSuccess={() => {
              setShowForgotPassword(false);
              setShowLogin(true);
              setShowRegister(false);
              setShowConfirm(false);
              setShowDashboard(false);

              window.scrollTo(0, 0);
            }}
          />
        )}

        {/* Registrierung */}
        {showRegister && (
          <SignUp
            onSuccess={() => {
              setShowRegister(false);
              setShowConfirm(true);
            }}
            onBackToLogin={() => {
              setShowRegister(false);
              setShowLogin(true);
            }}
          />
        )}

        {/* Bestätigungscode */}
        {showConfirm && (
          <ConfirmSignUp
            onSuccess={() => {
              setShowConfirm(false);
              setShowLogin(true);
            }}
            onBackToLogin={() => {
              setShowConfirm(false);
              setShowLogin(true);
            }}
          />
        )}

        {/* Dashboard */}
        {showDashboard &&
          !showEingabemaske &&
          !showUploadFile && (
            <GreenOneDashboardV2
              key={dashboardDataVersion}
              user={currentUser}
              onEingabemaskeClick={() => {
                window.location.href = "/datenerhebung";
              }}
              onLogout={async () => {
                await signOut();

                setCurrentUser(null);
                setPrefetchedData(null);
                setBuildingRelationship("");
                setShowDashboard(false);
                setDashboardDataVersion(0);
                setPendingDashboardRefreshVersion(null);
                setRenderedDashboardRefreshVersion(null);

                hideProcessingNotification();

                localStorage.removeItem(
                  "buildingRelationship"
                );

                localStorage.removeItem(
                  "emissionsCache"
                );

                sessionStorage.removeItem(
                  "greenOneDashboardSkeletonShown"
                );
              }}
            />
          )}

        {/* Eingabemaske */}
        {showEingabemaske && (
          <Eingabemaske
            user={currentUser}
            onBack={() =>
              setShowEingabemaske(false)
            }
            onSaved={
              handleSavedAndReturnToDashboard
            }
          />
        )}

        {/* UploadFile */}
        {showUploadFile && (
          <UploadFile
            user={currentUser}
            onBack={() =>
              setShowUploadFile(false)
            }
            onSaved={
              handleSavedAndReturnToDashboard
            }
          />
        )}
      </div>

      <AppFooter />
    </div>
  );
}

export default App;
